import { useEffect, useId, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";

export type AccessState = {
  loading: boolean;
  isSubscribed: boolean;
  isPastDue: boolean;
  status: string | null;
  generationCount: number;
  hasAccess: boolean; // subscribed OR under free quota
  freeQuota: number;
};

const FREE_QUOTA = 1;

export function useAccess(userId: string | null) {
  const instanceId = useId();
  const [state, setState] = useState<AccessState>({
    loading: true,
    isSubscribed: false,
    isPastDue: false,
    status: null,
    generationCount: 0,
    hasAccess: false,
    freeQuota: FREE_QUOTA,
  });

  async function refresh() {
    if (!userId) {
      setState({ loading: false, isSubscribed: false, isPastDue: false, status: null, generationCount: 0, hasAccess: false, freeQuota: FREE_QUOTA });
      return;
    }
    const env = getStripeEnvironment();
    const [{ data: sub }, { count }] = await Promise.all([
      supabase
        .from("subscriptions")
        .select("status,current_period_end")
        .eq("user_id", userId)
        .eq("environment", env)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("generations")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId),
    ]);

    let isSubscribed = false;
    let isPastDue = false;
    const status = (sub?.status as string | undefined) ?? null;
    if (sub) {
      const periodEnd = sub.current_period_end ? new Date(sub.current_period_end as string).getTime() : null;
      const future = periodEnd === null || periodEnd > Date.now();
      if (["active", "trialing", "past_due"].includes(sub.status as string) && future) isSubscribed = true;
      if (sub.status === "canceled" && periodEnd && periodEnd > Date.now()) isSubscribed = true;
      if (sub.status === "past_due" && future) isPastDue = true;
    }

    const generationCount = count ?? 0;
    const hasAccess = isSubscribed || generationCount < FREE_QUOTA;
    setState({ loading: false, isSubscribed, isPastDue, status, generationCount, hasAccess, freeQuota: FREE_QUOTA });
  }

  useEffect(() => {
    refresh();
    if (!userId) return;
    const channel = supabase
      .channel(`access-${userId}-${instanceId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${userId}` },
        () => refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return { ...state, refresh };
}
